export type ResolvedErrorReference = {
  /** The id an error surface shows: the backend `requestId`, or a minted
   *  client UUID when the backend never recorded the failure. */
  reference: string;
  /** True when the reference was minted client-side — the caller must then
   *  tie it to a record via `emitMintedReferenceLog`. */
  minted: boolean;
};

/**
 * The reference-source rule shared by every error surface: a backend
 * `requestId` is reused verbatim (the backend already recorded the error
 * under that id — no client log), anything else gets a minted
 * `crypto.randomUUID()`.
 */
export function resolveErrorReference(
  requestId: string | undefined,
): ResolvedErrorReference {
  return requestId !== undefined && requestId !== ''
    ? { reference: requestId, minted: false }
    : { reference: crypto.randomUUID(), minted: true };
}

/**
 * The one `console.error` that records a minted reference — forwarded to
 * Sentry as a log by `consoleLoggingIntegration`, stack-tagged by
 * `attachConsoleOrigin`. `context.reference` is required so the log always
 * correlates to the id shown on screen. No secrets / PII in `context`.
 */
export function emitMintedReferenceLog(
  logMessage: string,
  context: { reference: string } & Record<
    string,
    string | number | boolean | null | undefined
  >,
): void {
  console.error(logMessage, context);
}
